import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Palette, Type, Layout, Image as ImageIcon, Save, Eye, Menu as MenuIcon,
  Settings, Monitor, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUICustomizer({ onBack }) {
  const [uiSettings, setUiSettings] = useState({
    // Global Theme
    primaryColor: '#7C3AED',
    secondaryColor: '#EC4899',
    accentColor: '#F59E0B',
    backgroundColor: '#FFFFFF',
    textColor: '#1F2937',
    
    // Typography
    headingFont: 'Inter',
    bodyFont: 'Inter',
    fontSize: '16px',
    
    // Layout
    containerWidth: '1280px',
    borderRadius: '8px',
    spacing: 'normal',
    
    // Navigation
    navPosition: 'top',
    navStyle: 'default',
    showLogo: true,
    logoUrl: '',
    siteName: 'DigiDreamSolutions',
    
    // Components
    buttonStyle: 'rounded',
    cardStyle: 'shadow',
    inputStyle: 'outlined',
    
    // Homepage
    heroTitle: 'Welcome to DigiDreamSolutions',
    heroSubtitle: 'Your complete business suite',
    heroImage: '',
    showHeroSection: true,
    
    // Footer
    footerText: '© 2024 DigiDreamSolutions. All rights reserved.',
    showFooter: true,
    footerLinks: []
  });

  const [activeSection, setActiveSection] = useState('theme');

  useEffect(() => {
    loadUISettings();
  }, []);

  const loadUISettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/ui-settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (response.data.settings) {
        setUiSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Failed to load UI settings:', error);
    }
  };

  const saveUISettings = async () => {
    try {
      await axios.put(
        `${API}/admin/ui-settings`,
        uiSettings,
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      toast.success('UI settings saved! Refresh to see changes.');
    } catch (error) {
      toast.error('Failed to save UI settings');
    }
  };

  const sections = [
    { id: 'theme', name: 'Theme & Colors', icon: Palette },
    { id: 'typography', name: 'Typography', icon: Type },
    { id: 'layout', name: 'Layout', icon: Layout },
    { id: 'navigation', name: 'Navigation', icon: MenuIcon },
    { id: 'components', name: 'Components', icon: Settings },
    { id: 'homepage', name: 'Homepage', icon: Monitor }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">UI Customizer</h2>
          <p className="text-gray-500">Customize the entire website appearance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Eye className="w-4 h-4 mr-2" />
            Preview Changes
          </Button>
          <Button onClick={saveUISettings} className="bg-purple-600 hover:bg-purple-700">
            <Save className="w-4 h-4 mr-2" />
            Save & Apply
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="col-span-1">
          <Card>
            <CardContent className="p-4">
              <div className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        activeSection === section.id
                          ? 'bg-purple-100 text-purple-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm">{section.name}</span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Settings Panel */}
        <div className="col-span-3 space-y-6">
          {activeSection === 'theme' && (
            <Card>
              <CardHeader>
                <CardTitle>Theme & Colors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Primary Color</label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={uiSettings.primaryColor}
                        onChange={(e) => setUiSettings({ ...uiSettings, primaryColor: e.target.value })}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={uiSettings.primaryColor}
                        onChange={(e) => setUiSettings({ ...uiSettings, primaryColor: e.target.value })}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Secondary Color</label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={uiSettings.secondaryColor}
                        onChange={(e) => setUiSettings({ ...uiSettings, secondaryColor: e.target.value })}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={uiSettings.secondaryColor}
                        onChange={(e) => setUiSettings({ ...uiSettings, secondaryColor: e.target.value })}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Accent Color</label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={uiSettings.accentColor}
                        onChange={(e) => setUiSettings({ ...uiSettings, accentColor: e.target.value })}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={uiSettings.accentColor}
                        onChange={(e) => setUiSettings({ ...uiSettings, accentColor: e.target.value })}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">Background Color</label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={uiSettings.backgroundColor}
                        onChange={(e) => setUiSettings({ ...uiSettings, backgroundColor: e.target.value })}
                        className="w-16 h-10"
                      />
                      <Input
                        type="text"
                        value={uiSettings.backgroundColor}
                        onChange={(e) => setUiSettings({ ...uiSettings, backgroundColor: e.target.value })}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700 mb-3">Color Preview</p>
                  <div className="flex gap-4">
                    <div className="flex-1 p-4 rounded-lg text-white text-center" style={{ backgroundColor: uiSettings.primaryColor }}>
                      Primary
                    </div>
                    <div className="flex-1 p-4 rounded-lg text-white text-center" style={{ backgroundColor: uiSettings.secondaryColor }}>
                      Secondary
                    </div>
                    <div className="flex-1 p-4 rounded-lg text-white text-center" style={{ backgroundColor: uiSettings.accentColor }}>
                      Accent
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'typography' && (
            <Card>
              <CardHeader>
                <CardTitle>Typography</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Heading Font</label>
                  <select
                    value={uiSettings.headingFont}
                    onChange={(e) => setUiSettings({ ...uiSettings, headingFont: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Inter">Inter</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Poppins">Poppins</option>
                    <option value="Montserrat">Montserrat</option>
                    <option value="Playfair Display">Playfair Display</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Body Font</label>
                  <select
                    value={uiSettings.bodyFont}
                    onChange={(e) => setUiSettings({ ...uiSettings, bodyFont: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Inter">Inter</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Open Sans">Open Sans</option>
                    <option value="Lato">Lato</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Base Font Size</label>
                  <Input
                    type="text"
                    value={uiSettings.fontSize}
                    onChange={(e) => setUiSettings({ ...uiSettings, fontSize: e.target.value })}
                    placeholder="16px"
                  />
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700 mb-3">Typography Preview</p>
                  <div style={{ fontFamily: uiSettings.headingFont }}>
                    <h1 className="text-4xl font-bold mb-2">Heading 1</h1>
                    <h2 className="text-3xl font-bold mb-2">Heading 2</h2>
                    <h3 className="text-2xl font-bold mb-4">Heading 3</h3>
                  </div>
                  <div style={{ fontFamily: uiSettings.bodyFont, fontSize: uiSettings.fontSize }}>
                    <p>This is body text using {uiSettings.bodyFont} font family.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'navigation' && (
            <Card>
              <CardHeader>
                <CardTitle>Navigation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Site Name</label>
                  <Input
                    type="text"
                    value={uiSettings.siteName}
                    onChange={(e) => setUiSettings({ ...uiSettings, siteName: e.target.value })}
                    placeholder="DigiDreamSolutions"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Logo URL</label>
                  <Input
                    type="text"
                    value={uiSettings.logoUrl}
                    onChange={(e) => setUiSettings({ ...uiSettings, logoUrl: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Navigation Position</label>
                  <select
                    value={uiSettings.navPosition}
                    onChange={(e) => setUiSettings({ ...uiSettings, navPosition: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="top">Top</option>
                    <option value="side">Side</option>
                    <option value="sticky">Sticky</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={uiSettings.showLogo}
                    onChange={(e) => setUiSettings({ ...uiSettings, showLogo: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label className="text-sm font-medium text-gray-700">Show Logo</label>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'homepage' && (
            <Card>
              <CardHeader>
                <CardTitle>Homepage Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    checked={uiSettings.showHeroSection}
                    onChange={(e) => setUiSettings({ ...uiSettings, showHeroSection: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label className="text-sm font-medium text-gray-700">Show Hero Section</label>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Hero Title</label>
                  <Input
                    type="text"
                    value={uiSettings.heroTitle}
                    onChange={(e) => setUiSettings({ ...uiSettings, heroTitle: e.target.value })}
                    placeholder="Welcome to DigiDreamSolutions"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Hero Subtitle</label>
                  <Input
                    type="text"
                    value={uiSettings.heroSubtitle}
                    onChange={(e) => setUiSettings({ ...uiSettings, heroSubtitle: e.target.value })}
                    placeholder="Your complete business suite"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Hero Image URL</label>
                  <Input
                    type="text"
                    value={uiSettings.heroImage}
                    onChange={(e) => setUiSettings({ ...uiSettings, heroImage: e.target.value })}
                    placeholder="https://example.com/hero.jpg"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
